import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Upload, FileSearch, CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";
import { ClaimEstimateCard } from "@/components/job-detail/claims/ClaimEstimateCard";

// Set PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface ClaimReaderTabProps {
  jobId: string;
}

interface CoverageItem {
  item: string;
  status: "covered" | "limited" | "excluded";
  notes: string;
}

interface ClaimAnalysis {
  policy_number: string;
  claim_number: string;
  coverage_amount: number;
  deductible: number;
  rcv?: number;
  acv?: number;
  coverage_items: CoverageItem[];
  insights: string;
}

export function ClaimReaderTab({ jobId }: ClaimReaderTabProps) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [analysis, setAnalysis] = useState<ClaimAnalysis | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [latestEstimateId, setLatestEstimateId] = useState<string | null>(null);

  useEffect(() => {
    loadLatestAnalysis();
    loadLatestEstimate();
  }, [jobId]);

  const loadLatestEstimate = async () => {
    const { data } = await supabase
      .from("claim_estimates")
      .select("id")
      .eq("job_id", jobId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setLatestEstimateId(data?.id ?? null);
  };

  const loadLatestAnalysis = async () => {
    try {
      const { data, error } = await supabase
        .from("claim_analyses")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error loading analysis:", error);
        return;
      }
      if (data) {
        setAnalysis({
          policy_number: data.policy_number || "",
          claim_number: data.claim_number || "",
          coverage_amount: data.coverage_amount || 0,
          deductible: data.deductible || 0,
          rcv: data.rcv || undefined,
          acv: data.acv || undefined,
          coverage_items: (data.coverage_items as unknown as CoverageItem[]) || [],
          insights: data.insights || "",
        });
      }
    } catch (error) {
      console.error("Error loading analysis:", error);
    }
  };

  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item: any) => item.str).join(" ");
      fullText += pageText + "\n";
      setProgress((i / pdf.numPages) * 50);
    }

    return fullText;
  };

  const extractTextFromImage = async (file: File): Promise<string> => {
    const result = await Tesseract.recognize(file, "eng", {
      logger: (m) => {
        if (m.status === "recognizing text") {
          setProgress(25 + m.progress * 25);
        }
      },
    });
    return result.data.text;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (selectedFile.size > maxSize) {
      toast.error("File size exceeds 20MB limit");
      return;
    }

    setFile(selectedFile);
    setLoading(true);
    setProgress(0);

    try {
      // Step 1: Extract text from document (client-side)
      let documentText = "";
      if (selectedFile.type === "application/pdf") {
        documentText = await extractTextFromPDF(selectedFile);
      } else if (selectedFile.type.startsWith("image/")) {
        documentText = await extractTextFromImage(selectedFile);
      } else {
        toast.error("Unsupported file type. Please upload PDF or image.");
        setLoading(false);
        return;
      }

      setProgress(60);

      // Step 2: Upload file to storage
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) throw new Error("Organization not found");

      const timestamp = Date.now();
      const filePath = `${profile.org_id}/${jobId}/claims/${timestamp}_${selectedFile.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from("claim-documents")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("claim-documents")
        .getPublicUrl(filePath);

      setProgress(70);

      // Step 3: Call edge function with TEXT ONLY
      const { data: parseData, error: parseError } = await supabase.functions.invoke(
        "parse-claim-document",
        { body: { documentText, jobId, orgId: profile.org_id, documentName: selectedFile.name, documentUrl: publicUrl } }
      );

      if (parseError) {
        console.error('Claim parsing error:', parseError);
        toast.error("Failed to analyze claim document");
        throw parseError;
      }

      if (!parseData || parseData.error) {
        console.error('Claim parsing returned error:', parseData?.error);
        toast.error(parseData?.error || "Failed to analyze claim document");
        throw new Error(parseData?.error || "Failed to analyze claim");
      }

      setProgress(100);

      // Step 4: Update UI with results
      setAnalysis(parseData.analysis);
      toast.success("✅ Claim document analyzed successfully!");

      // Reset file input to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error("Error processing claim document:", error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process claim document';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "covered":
        return <CheckCircle2 className="w-5 h-5 text-success" />;
      case "limited":
        return <AlertTriangle className="w-5 h-5 text-warning" />;
      case "excluded":
        return <XCircle className="w-5 h-5 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case "covered":
        return "bg-success/10";
      case "limited":
        return "bg-warning/10";
      case "excluded":
        return "bg-destructive/10";
      default:
        return "bg-muted/50";
    }
  };

  return (
    <div className="space-y-6">
      {latestEstimateId && (
        <ClaimEstimateCard
          claimEstimateId={latestEstimateId}
          onDeleted={() => { setLatestEstimateId(null); loadLatestEstimate(); }}
        />
      )}
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Claim Document
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="block">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={loading}
              />
              <div className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer" onClick={() => !loading && fileInputRef.current?.click()}>
                <FileSearch className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">
                  {file ? file.name : "Drop document here or click to upload"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Supports PDF, JPG, PNG - Max 20MB
                </p>
                {!loading && (
                  <Button 
                    variant="outline" 
                    type="button" 
                    onClick={(e) => { 
                      e.preventDefault(); 
                      e.stopPropagation(); 
                      fileInputRef.current?.click(); 
                    }}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select Document
                  </Button>
                )}
              </div>
            </label>

            {loading && (
              <div className="space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-center font-medium text-muted-foreground">
                  {progress < 50 ? "📄 Extracting text from document..." : 
                   progress < 70 ? "☁️ Uploading to storage..." :
                   progress < 90 ? "🤖 AI is analyzing claim document..." : "✨ Finalizing..."}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Parsed Policy Fields</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-medium">Policy Number</span>
                    <Badge variant="outline">Detected</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{analysis.policy_number || "N/A"}</p>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-medium">Claim Number</span>
                    <Badge variant="outline">Detected</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{analysis.claim_number || "N/A"}</p>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-medium">Coverage Amount</span>
                    <Badge variant="outline">Detected</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {analysis.coverage_amount ? `$${analysis.coverage_amount.toLocaleString()}` : "N/A"}
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-medium">Deductible</span>
                    <Badge variant="outline">Detected</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {analysis.deductible ? `$${analysis.deductible.toLocaleString()}` : "N/A"}
                  </p>
                </div>

                {analysis.rcv && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm font-medium">RCV</span>
                      <Badge variant="outline">Detected</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">${analysis.rcv.toLocaleString()}</p>
                  </div>
                )}

                {analysis.acv && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm font-medium">ACV</span>
                      <Badge variant="outline">Detected</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">${analysis.acv.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Coverage Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analysis.coverage_items.map((item, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${getStatusBg(item.status)}`}>
                    {getStatusIcon(item.status)}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.item}</p>
                      {item.notes && <p className="text-xs text-muted-foreground">{item.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {analysis && analysis.insights && (
        <Card>
          <CardHeader>
            <CardTitle>AI Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{analysis.insights}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
